export type MoneyLanguage = 'en' | 'th';

export type MoneyFractionDigits = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface MoneyFormatOptions {
  readonly fractionDigits?: MoneyFractionDigits;
}

const numberLocale: Record<MoneyLanguage, string> = {
  en: 'en-US-u-nu-latn',
  th: 'th-TH-u-nu-latn',
};

export function formatMoney(
  amount: string | number,
  currency: string,
  language: MoneyLanguage = 'en',
  options: MoneyFormatOptions = {},
): string {
  const currencyCode = currency.trim().toUpperCase();
  const fractionDigits = options.fractionDigits ?? 2;

  if (typeof amount === 'string') {
    const exact = formatExactDecimal(amount, language, fractionDigits);
    if (exact === null) return `${currencyCode}\u00a0${amount}`;
    return `${exact.negative ? '-' : ''}${currencyCode}\u00a0${exact.amount}`;
  }

  if (!Number.isFinite(amount)) return `${currencyCode}\u00a0${String(amount)}`;

  const negative = amount < 0 || Object.is(amount, -0);
  const formattedAmount = new Intl.NumberFormat(numberLocale[language], {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    useGrouping: true,
  }).format(Math.abs(amount));

  return `${negative ? '-' : ''}${currencyCode}\u00a0${formattedAmount}`;
}

function formatExactDecimal(
  value: string,
  language: MoneyLanguage,
  fractionDigits: MoneyFractionDigits,
): { readonly amount: string; readonly negative: boolean } | null {
  const match = /^([+-]?)(\d{1,20})(?:\.(\d{1,6}))?$/u.exec(value);
  if (match === null) return null;
  const integer = match[2];
  if (integer === undefined) return null;
  const rounded = roundExactDecimal(integer, match[3] ?? '', fractionDigits);
  const formatter = new Intl.NumberFormat(numberLocale[language], {
    maximumFractionDigits: 0,
    useGrouping: true,
  });
  const decimalSeparator =
    new Intl.NumberFormat(numberLocale[language], { minimumFractionDigits: 1 })
      .formatToParts(0.1)
      .find((part) => part.type === 'decimal')?.value ?? '.';
  return {
    amount: `${formatter.format(BigInt(rounded.integer))}${
      rounded.fraction.length === 0 ? '' : `${decimalSeparator}${rounded.fraction}`
    }`,
    negative: match[1] === '-',
  };
}

function roundExactDecimal(
  integer: string,
  fraction: string,
  fractionDigits: MoneyFractionDigits,
): { readonly integer: string; readonly fraction: string } {
  if (fraction.length <= fractionDigits) {
    return { integer, fraction: fraction.padEnd(fractionDigits, '0') };
  }

  const discardedDigits = fraction.length - fractionDigits;
  const divisor = 10n ** BigInt(discardedDigits);
  const magnitude = BigInt(`${integer}${fraction}`);
  const roundedMagnitude = (magnitude + divisor / 2n) / divisor;
  const scale = 10n ** BigInt(fractionDigits);
  const roundedInteger = roundedMagnitude / scale;
  const roundedFraction =
    fractionDigits === 0 ? '' : String(roundedMagnitude % scale).padStart(fractionDigits, '0');

  return { integer: String(roundedInteger), fraction: roundedFraction };
}
